import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Alert,
    Spinner,
} from "react-bootstrap";
import { Input } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader";

import { emailTemplatesForm } from "../../configs/Forms/AdvancedForms";
import {
    getEmailTemplate,
    insertEmailTemplate,
    updateEmailTemplate,
} from "./emailTempService";
import { serverHost } from "variables/serverdata";
import { getAuthToken } from "../../util/helpers";

const titleCase = (s) =>
    s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const EmailTempAddEdit = () => {
    const { email_template_uuid } = useParams();
    const navigate = useNavigate();
    const editing = Boolean(email_template_uuid);

    const [emailTemp, setEmailTemp] = useState({});
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(editing);
    const [error, setError] = useState("");

    // ----------------------------
    // FETCH DOMAINS
    // ----------------------------
    useEffect(() => {
        const fetchDomains = async () => {
            try {
                const token = getAuthToken();
                if (!token) return;
                const res = await fetch(
                    `${serverHost}/get_rows?table=v_domains&page=1&pageSize=1000`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const data = await res.json();
                if (Array.isArray(data.results)) setDomains(data.results);
            } catch (err) {
                console.error("Error fetching domains:", err);
            }
        };
        fetchDomains();
    }, []);

    // ----------------------------
    // FETCH EXISTING TEMPLATE
    // ----------------------------
    useEffect(() => {
        if (!editing) return;

        const fetchTemplate = async () => {
            try {
                setLoading(true);
                const data = await getEmailTemplate(email_template_uuid);
                if (data.error) {
                    setError(data.error);
                    return;
                }

                setEmailTemp({
                    email_template_uuid: data.result.email_template_uuid,
                    template_language: data.result.language,
                    template_category: data.result.category,
                    template_subcategory: data.result.subcategory,
                    template_subject: data.result.subject,
                    template_body: data.result.body,
                    domain_uuid: data.result.domain_uuid || "",
                    template_type: data.result.type,
                    template_enabled: data.result.enabled,
                    template_description: data.result.description || "",
                });
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchTemplate();
    }, [email_template_uuid, editing]);

    // ----------------------------
    // HELPERS
    // ----------------------------
    const handleChange = (field, value) =>
        setEmailTemp((prev) => ({ ...prev, [field]: value }));

    const handleSave = async () => {
        setError("");
        const missing = [];
        const user = JSON.parse(localStorage.getItem("user"));
        const user_uuid = user?.user_uuid || null;
        const site = JSON.parse(localStorage.getItem("selectedSite"));

        // Use selected site's UUID if no domain is explicitly selected
        const domain_uuid = emailTemp.domain_uuid || site?.site_uuid;

        // Check required fields (skip domain_uuid since we have a fallback)
        emailTemplatesForm.required.forEach((field) => {
            if (field === "domain_uuid") return;
            if (!emailTemp[field]) missing.push(field);
        });

        if (missing.length) {
            setError(`Required fields missing: ${missing.join(", ")}`);
            return;
        }

        try {
            const sanitized = {};

            // Include all fields, using defaults if necessary
            Object.keys(emailTemp).forEach((key) => {
                let value = emailTemp[key];

                // fallback to default if specified in form config
                if (value === undefined || value === null || value === "") {
                    value = emailTemplatesForm.defaults?.[key] ?? null;
                }

                sanitized[key] = value;
            });

            // Ensure domain_uuid is always included
            sanitized.domain_uuid = domain_uuid;

            if (editing) {
                await updateEmailTemplate(sanitized, domain_uuid, user_uuid);
            } else {
                await insertEmailTemplate(sanitized, domain_uuid, user_uuid);
            }
            navigate("/admin/hucs/emailTemplates");
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to save");
        }
    };

    const renderField = (field, value, onChange) => {
        const type = emailTemplatesForm.types[field];
        const options = emailTemplatesForm.options?.[field];
        const label =
            emailTemplatesForm.labels?.[field] || titleCase(field);

        const placeholder = value
            ? ""
            : `Enter ${label.toLowerCase()}`;

        // BOOLEAN
        if (type === "boolean") {
            return (
                <Input
                    type="select"
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                >
                    <option value="true">True</option>
                    <option value="false">False</option>
                </Input>
            );
        }

        // SELECT
        if (type === "select") {
            return (
                <Input
                    type="select"
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                >

                    {options.map((opt) =>
                        typeof opt === "object" ? (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ) : (
                            <option key={opt} value={opt}>
                                {opt}
                            </option>
                        )
                    )}
                </Input>
            );
        }

        if (type === "textarea") {
            return (
                <Form.Control
                    as="textarea"
                    rows={10}
                    value={value ?? ""}
                    onChange={onChange}
                    style={{
                        fontFamily: "monospace",
                        fontSize: "0.95rem",
                        padding: "0.5rem",
                        whiteSpace: "pre-wrap",
                        resize: "both",
                        minWidth: "100%",
                        overflow: "auto",
                    }}
                />
            );
        }


        return (
            <Form.Control
                value={value ?? ""}
                onChange={onChange}
                placeholder={placeholder}
            />
        );
    };

    if (loading)
        return (
            <div className="text-center mt-5">
                <Spinner animation="border" />
                <p className="mt-2">Loading Email Template...</p>
            </div>
        );

    // ----------------------------
    // RENDER
    // ----------------------------
    return (
        <>
            <SimpleHeader
                name={`${editing ? "Edit" : "Add"} Email Template`}
                parentName="Email Templates"
            />

            <Container className="mt--6">
                <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
                    <Card.Header className="header-theme">
                        <h4 className="mb-0">
                            {editing ? "EDIT EMAIL TEMPLATE" : "ADD EMAIL TEMPLATE"}
                        </h4>
                    </Card.Header>

                    <Card.Body className="p-4">
                        {error && <Alert variant="danger">{error}</Alert>}

                        <Form>
                            <Row className="mb-2">
                                {emailTemplatesForm.sections["Basic Info"].map((field, i) => (
                                    <Col md={4} key={i}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                {emailTemplatesForm.labels[field] || field}
                                                {emailTemplatesForm.required.includes(field) && (
                                                    <span style={{ color: "red" }}> *</span>
                                                )}
                                            </Form.Label>

                                            {field === "domain_uuid" ? (
                                                <Input
                                                    type="select"
                                                    value={emailTemp[field] ?? ""}
                                                    onChange={(e) => handleChange(field, e.target.value)}
                                                >
                                                    <option value="">Select Domain</option>
                                                    {domains.map((d) => (
                                                        <option key={d.domain_uuid} value={d.domain_uuid}>
                                                            {d.domain_name}
                                                        </option>
                                                    ))}
                                                </Input>
                                            ) : (
                                                renderField(
                                                    field,
                                                    emailTemp[field],
                                                    (e) => handleChange(field, e.target.value)
                                                )
                                            )}
                                        </Form.Group>
                                    </Col>
                                ))}
                            </Row>

                            <div className="mt-4 d-flex justify-content-between">
                                <Button
                                    variant="danger"
                                    onClick={() => navigate("/admin/hucs/email-templates")}
                                >
                                    Cancel
                                </Button>
                                <Button variant="success" onClick={handleSave}>
                                    Save
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
        </>
    );
};

export default EmailTempAddEdit;
